/**
 * What kind of battle this is.
 *
 * A mode is the small set of rules that turn a chart and two lines of ships
 * into a match with a winner: how long it runs, which of the chart's rings it
 * is fought over, what a ring and a sinking are worth, and how many points end
 * it. Everything else — helm, guns, damage, vision, the rings' own capture
 * maths — is the same in every mode, and lives where it always did.
 *
 * The arena is handed one of these at construction (`ArenaOptions.mode`) and
 * reads it, never the constants in `objectives.ts` directly, so that adding a
 * mode is a row in `MODES` and not a search through the simulation. The wire
 * carries the mode id in every snapshot and phase event, and the client looks
 * the rules up from the same table: which rings to draw and what cap to show
 * are derived, not sent.
 *
 * Five rows: `domination` is the game as it was before there were modes;
 * `encounter` is one ring in the middle; `annihilation` has no rings at all;
 * `epicentre` is three rings one inside the other, the inner one worth most;
 * `convoy` has no rings either and is decided by three merchantmen
 * (`convoy.ts`) that team A escorts to a port behind team B's line.
 *
 * ── Which mode a match is ───────────────────────────────────────────────────
 *
 * An arena is either pinned to one mode for its life, or rotates: then each
 * match draws its mode from the match seed (`modeForSeed`), the way it draws
 * its chart and weather, so every client can name the mode from the seed
 * alone. Rotation is what a single-arena server runs; a server with an arena
 * per mode pins each one.
 */
import { CONVOY, type ConvoyRules } from './convoy';
import type { IslandLayout } from './islands';
import { POINTS_PER_KILL, POINTS_PER_ZONE_PER_SECOND, VICTORY_POINTS, type ZoneLayout } from './objectives';

export type MatchMode = 'domination' | 'encounter' | 'annihilation' | 'epicentre' | 'convoy';

export const MATCH_MODES: readonly MatchMode[] = ['domination', 'encounter', 'annihilation', 'epicentre', 'convoy'];

/** `ArenaOptions.mode` and `ARENA_MODE` take a mode, or this to draw one a match. */
export const ROTATE_MODES = 'rotate';
export type ModeChoice = MatchMode | typeof ROTATE_MODES;

export const DEFAULT_MODE: MatchMode = 'domination';

export interface ModeRules {
  id: MatchMode;
  /** Length of the `battle` phase. The clock is one of two ways a battle ends. */
  battleSeconds: number;
  /**
   * Which of the chart's rings this mode is fought over, in wire order. A
   * mode with no rings returns an empty list and is decided by sinkings and
   * the clock alone.
   */
  rings(layout: IslandLayout): readonly ZoneLayout[];
  /** Points the winner has to reach; whoever gets here first ends the battle. */
  victoryPoints: number;
  /** Points a team earns per second, per ring it holds. */
  pointsPerZonePerSecond: number;
  /** Points for putting an enemy on the bottom. */
  pointsPerKill: number;
  /** Set on a mode with a convoy to escort (`convoy.ts`); the arena then sails merchants. */
  convoy?: ConvoyRules;
}

/**
 * The game as it was before there were modes: ten minutes, every ring on the
 * chart, first to a thousand or the last fleet afloat. The numbers are the
 * ones `objectives.ts` was written around; see its notes on how they were
 * set against a ten-minute battle of six a side.
 */
const DOMINATION: ModeRules = {
  id: 'domination',
  battleSeconds: 600,
  rings: (layout) => layout.zones,
  victoryPoints: VICTORY_POINTS,
  pointsPerZonePerSecond: POINTS_PER_ZONE_PER_SECOND,
  pointsPerKill: POINTS_PER_KILL,
};

/**
 * One ring, in the middle of the chart, eight minutes. The ring pays more than
 * a domination ring because it is the only one: holding it the whole battle
 * is 960 points, so a team that takes the middle early and keeps it reaches
 * the cap of 750 around the six-minute mark, and a team that loses it has
 * sinkings to make up the difference with.
 */
const ENCOUNTER: ModeRules = {
  id: 'encounter',
  battleSeconds: 480,
  rings: (layout) => [centreRing(layout)],
  victoryPoints: 750,
  pointsPerZonePerSecond: 2,
  pointsPerKill: POINTS_PER_KILL,
};

/**
 * No rings. Twelve minutes, sink everything, or lead on sinkings when the
 * clock runs out; the cap is a full line of six put on the bottom, which the
 * elimination rule already ends the battle for. The mode the open-water
 * stand-off in `objectives.ts` was written against — kept because a plain
 * fight is what some captains put to sea for, and because nothing forces
 * anybody forward here except the clock and the other line's patience.
 */
const ANNIHILATION: ModeRules = {
  id: 'annihilation',
  battleSeconds: 720,
  rings: () => [],
  victoryPoints: 600,
  pointsPerZonePerSecond: 0,
  pointsPerKill: 100,
};

/** Epicentre's rings, inner first so `zoneAt` names the innermost a ship stands in. */
export const EPICENTRE_RADII: readonly [number, number, number] = [250, 450, 650];
const EPICENTRE_WEIGHTS: readonly [number, number, number] = [3, 2, 1];

/**
 * Three rings one inside the other round the middle of the chart, the inner
 * worth three of the outer. A hull in the inner ring is inside all three, so
 * the team that owns the middle collects from every ring — 3.6 a second, the
 * pace of a domination team holding all three of its rings — while a team
 * that can only reach the outer one collects 0.6 and has to go in. The radii
 * are the widest that fit every chart's centre ring clear of the coast
 * (`modes.test` checks; the headland's is 700 m off); the ring size is not
 * `ZONE_RADIUS` here.
 */
const EPICENTRE: ModeRules = {
  id: 'epicentre',
  battleSeconds: 600,
  rings: (layout) => {
    const c = centreRing(layout);
    const ids: readonly ZoneLayout['id'][] = ['inner', 'middle', 'outer'];
    return ids.map((id, i) => ({ id, x: c.x, z: c.z, radius: EPICENTRE_RADII[i]!, near: null, weight: EPICENTRE_WEIGHTS[i]! }));
  },
  victoryPoints: VICTORY_POINTS,
  pointsPerZonePerSecond: 0.6,
  pointsPerKill: POINTS_PER_KILL,
};

/** The chart's ring that is nobody's, or its first if it has no such ring. */
function centreRing(layout: IslandLayout): ZoneLayout {
  return layout.zones.find((z) => z.near === null) ?? layout.zones[0]!;
}

/**
 * Three merchantmen from behind A's line to a port behind B's, eleven
 * minutes. No rings; 250 a merchant either way against a cap of 750, so the
 * convoy is the match, and an ordinary sinking is still 75. The clock is
 * long enough for a merchant at 12 m/s to cross the classic chart with two
 * minutes to spare and to go round the atoll's reef with one; a convoy that
 * is held up is a convoy that has been fought, which is the point.
 */
const CONVOY_MODE: ModeRules = {
  id: 'convoy',
  battleSeconds: 660,
  rings: () => [],
  victoryPoints: 750,
  pointsPerZonePerSecond: 0,
  pointsPerKill: POINTS_PER_KILL,
  convoy: CONVOY,
};

export const MODES: Readonly<Record<MatchMode, ModeRules>> = {
  domination: DOMINATION,
  encounter: ENCOUNTER,
  annihilation: ANNIHILATION,
  epicentre: EPICENTRE,
  convoy: CONVOY_MODE,
};

export function modeRules(id: MatchMode): ModeRules {
  return MODES[id] ?? DOMINATION;
}

/** True for a string the wire may carry as a mode id. */
export function isMatchMode(value: unknown): value is MatchMode {
  return typeof value === 'string' && (MATCH_MODES as readonly string[]).includes(value);
}

/** True for a mode id or the word that asks for one a match. */
export function isModeChoice(value: unknown): value is ModeChoice {
  return value === ROTATE_MODES || isMatchMode(value);
}

/**
 * The mode a rotating arena plays for this seed. Not the same divisor as the
 * chart's (`layoutForSeed`, eight) so the pairing changes: with five modes
 * and eight charts every mode is seen on every chart over a run of seeds.
 * The seed is hashed per match (`Arena.seedFor`), so the sequence is not
 * simply mode after mode in table order.
 */
export function modeForSeed(seed: number): MatchMode {
  // A different slice of the seed than the chart uses, so the two do not lock step.
  const i = Math.abs((seed | 0) >>> 3) % MATCH_MODES.length;
  return MATCH_MODES[i]!;
}
